/**
 * VerifiedBadge  — shown for orgs with is_claimed = true
 * UnverifiedBadge — shown for orgs with is_claimed = false
 *
 * Both scale with surrounding text (height = 1em).
 * Usage:
 *   <VerifiedBadge />
 *   <UnverifiedBadge orgId="..." orgName="..." />
 */
import { useState, useRef, useEffect } from 'react';
import ClaimOrgModal from './ClaimOrgModal';

// ── Shared SVG badge shape ────────────────────────────────────────────────
// 12-point seal path on a 24×24 viewBox (Material-style verified badge)
const BADGE_PATH =
  'M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82 1.89 3.2L12 21.04l3.4 1.47 1.89-3.2 3.61-.82-.34-3.69L23 12z';

// ── VerifiedBadge ─────────────────────────────────────────────────────────
export const VerifiedBadge = ({ className = '' }) => {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  const trigger = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    setShow(true);
    timerRef.current = setTimeout(() => setShow(false), 2000);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <span
      className={`relative inline-flex items-center justify-center cursor-pointer flex-shrink-0 ${className}`}
      style={{ height: '1.1em', width: '1.1em', verticalAlign: 'middle' }}
      onMouseEnter={trigger}
      onTouchStart={trigger}
      onClick={trigger}
      data-testid="verified-badge"
      role="img"
      aria-label="Verified on Karmana"
    >
      {/* Badge SVG */}
      <svg
        viewBox="0 0 24 24"
        style={{ height: '100%', width: '100%', display: 'block' }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="karmana-verified-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4f46e5" />
            <stop offset="55%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#c026d3" />
          </linearGradient>
        </defs>
        <path d={BADGE_PATH} fill="url(#karmana-verified-grad)" />
        {/* White checkmark */}
        <path
          d="M9 12l2.25 2.25L15.5 9.5"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {/* Tooltip */}
      {show && (
        <span
          className="pointer-events-none absolute z-[500] whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1 text-[11px] font-medium text-white shadow-lg"
          style={{ bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)' }}
        >
          Verified on Karmana
          {/* Arrow */}
          <span
            className="absolute left-1/2 -translate-x-1/2 top-full"
            style={{
              width: 0, height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '5px solid #111827',
            }}
          />
        </span>
      )}
    </span>
  );
};

// ── UnverifiedBadge ───────────────────────────────────────────────────────
export const UnverifiedBadge = ({ orgId, orgName }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const timerRef = useRef(null);

  const trigger = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowPopover(true);
    timerRef.current = setTimeout(() => setShowPopover(false), 4000);
  };

  const handleClaim = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowPopover(false);
    setShowModal(true);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return (
    <>
      <span
        className="relative inline-flex items-center justify-center cursor-pointer flex-shrink-0"
        style={{ height: '1.1em', width: '1.1em', verticalAlign: 'middle' }}
        onMouseEnter={trigger}
        onTouchStart={trigger}
        onClick={trigger}
        data-testid="unverified-badge"
        role="button"
        aria-label="Unverified organisation — click to claim"
      >
        {/* Outline-only badge SVG */}
        <svg
          viewBox="0 0 24 24"
          style={{ height: '100%', width: '100%', display: 'block' }}
          aria-hidden="true"
        >
          <path
            d={BADGE_PATH}
            fill="none"
            stroke="#9ca3af"
            strokeWidth="1.5"
          />
          {/* Question mark / empty center indicator */}
          <text
            x="12" y="16"
            textAnchor="middle"
            fill="#9ca3af"
            fontSize="9"
            fontWeight="bold"
            fontFamily="sans-serif"
          >?</text>
        </svg>

        {/* Popover */}
        {showPopover && (
          <span
            className="absolute z-[500] flex flex-col items-center"
            style={{ bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="whitespace-nowrap rounded-lg bg-white border border-gray-200 px-3 py-1.5 shadow-lg">
              <button
                type="button"
                onClick={handleClaim}
                className="text-[12px] font-semibold text-green-600 hover:text-green-700 hover:underline"
                data-testid="claim-page-cta"
              >
                Claim this page.
              </button>
            </span>
            {/* Arrow */}
            <span
              style={{
                width: 0, height: 0,
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                borderTop: '5px solid #e5e7eb',
                marginTop: '-1px',
              }}
            />
          </span>
        )}
      </span>

      {showModal && (
        <ClaimOrgModal
          orgId={orgId}
          orgName={orgName}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
